import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import Image from '@atlaskit/image';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal, { CloseButton } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import type { PaywallButtonActionTypes } from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';

import type { ChangeEditionSectionMessageProps } from '../SectionMessage/ChangeEditionSectionMessage.tsx';
import { ConfluenceEdition } from '../Upgrade/__types__/waitForEditionChangeQuery.ts';
import { Card } from '../UpsellLooknFeel/Card.tsx';

import WhiteboardsPaywallImg from './images/WhiteboardsPaywall.svg';
import WhiteboardsPaywallDarkImg from './images/WhiteboardsPaywallDark.svg';
import WhiteboardsPaywallImgV2 from './images/WhiteboardsPaywallV2.svg';
import WhiteboardsPaywallDarkImgV2 from './images/WhiteboardsPaywallDarkV2.svg';
import { useEnableEndUserRequestUpgrade } from '@confluence/experiment-confluence-enable-end-user-request/entry-points/useEnableEndUserRequestUpgrade.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardWrapper = styled.div({
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: `0 0 ${token('radius.small', '3px')} ${token('radius.small', '3px')}`,
});

type RequiredSharedProps = Required<
	Pick<
		ChangeEditionSectionMessageProps,
		'learnMoreLink' | 'secondaryLink' | 'source' | 'title' | 'upgradeEditionActionSubjectId'
	>
>;

export type WhiteboardsActiveBoardLimitModalProps = React.PropsWithChildren<
	RequiredSharedProps & {
		learnMoreLink: Required<
			Pick<RequiredSharedProps['learnMoreLink'], 'href' | 'messageDescriptor'>
		>;
		onClose?: (action?: PaywallButtonActionTypes) => void;
		onCloseComplete?: () => void;
	}
>;

const getLinkAppearance = (
	isSiteAdmin: boolean,
	secondaryLink: WhiteboardsActiveBoardLimitModalProps['secondaryLink'],
	endUserRequestUpgradeButtonExists: boolean,
) => {
	if (endUserRequestUpgradeButtonExists) {
		return 'default';
	} else if (secondaryLink.appearance) {
		return secondaryLink.appearance;
	}
	return isSiteAdmin ? 'link' : 'primary';
};

const WhiteboardsActiveBoardLimitModalContents = ({
	children,
	learnMoreLink,
	secondaryLink,
	onClose,
	source,
	title,
	upgradeEditionActionSubjectId,
}: WhiteboardsActiveBoardLimitModalProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { showRequestUpgradeButton, loading } = useEnableEndUserRequestUpgrade(source);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const fireLearnMoreClickedAnalytics = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: learnMoreLink.actionSubjectId,
				source,
			},
		}).fire();
	};

	const showLearnMoreButton = isSiteAdmin && !fg('ten_active_boards_in_standard');

	return (
		<CardWrapper>
			<Card
				body={<>{children}</>}
				isLoadingRequestUpgradeButton={loading}
				showRequestUpgradeButton={showRequestUpgradeButton}
				learnMoreLink={
					showRequestUpgradeButton
						? undefined
						: {
								...learnMoreLink,
								appearance: learnMoreLink.appearance || 'link',
							}
				}
				secondaryLink={{
					...secondaryLink,
					appearance: getLinkAppearance(
						isSiteAdmin,
						secondaryLink,
						Boolean(showRequestUpgradeButton),
					),
				}}
				source={source}
				targetEdition={ConfluenceEdition.PREMIUM}
				title={title}
				upgradeEditionActionSubjectId={upgradeEditionActionSubjectId}
				headerButton={
					<>
						{showLearnMoreButton && (
							<Tooltip content={formatMessage(learnMoreLink.messageDescriptor)}>
								{(tooltipProps) => (
									<Button
										appearance="subtle-link"
										href={learnMoreLink.href}
										iconBefore={
											<QuestionCircleIcon
												label={formatMessage(learnMoreLink.messageDescriptor)}
												color={token('color.text.subtle')}
											/>
										}
										spacing="none"
										target="_blank"
										{...tooltipProps}
										onClick={(event) => {
											tooltipProps?.onClick?.(event);
											fireLearnMoreClickedAnalytics();
										}}
									/>
								)}
							</Tooltip>
						)}
						<CloseButton
							testId="whiteboards-active-board-modal-close-button"
							onClick={() => {
								onClose?.('close');
							}}
						/>
					</>
				}
			>
				<Image
					src={
						fg('ten_active_boards_in_standard') ? WhiteboardsPaywallImgV2 : WhiteboardsPaywallImg
					}
					srcDark={
						fg('ten_active_boards_in_standard')
							? WhiteboardsPaywallDarkImgV2
							: WhiteboardsPaywallDarkImg
					}
					alt=""
					width="100%"
				/>
			</Card>
		</CardWrapper>
	);
};

const WrappedWhiteboardsActiveBoardLimitModalContents = withErrorBoundary({
	attribution: Attribution.CONTENT_TYPES,
})(WhiteboardsActiveBoardLimitModalContents);

export const WhiteboardsActiveBoardLimitModal = (props: WhiteboardsActiveBoardLimitModalProps) => (
	<Modal
		onClose={() => props.onClose?.('close')}
		onCloseComplete={props.onCloseComplete}
		shouldScrollInViewport
		testId="whiteboards-upsell-modal"
		width="440px"
	>
		<WrappedWhiteboardsActiveBoardLimitModalContents {...props} />
	</Modal>
);
